import React from 'react';
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { PageContentWrapper, PageHeader } from '../main/LayoutStyles';
import styled from 'styled-components';
import { ShieldCheck, UserPlus, Building, Settings, List, Brush, Users, ToggleRight } from 'lucide-react';

// Importy komponentów
import PendingProfiles from './PendingProfiles';
import OrganizationManagement from './OrganizationManagement';
import OrganizationDetail from './OrganizationDetail';
import InviteForm from './InviteForm';
import RolePermissionManagement from './RolePermissionManagement';
import SystemFeatureManagement from './SystemFeatureManagement';
import BottomNavManagement from './BottomNavManagement';
import ThemeManagement from './ThemeManagement';

const AdminLayout = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem; /* Zmniejszony odstęp */
`;

const AdminNav = styled.nav`
    display: none;

    @media (min-width: 768px) {
        display: flex;
        gap: 1rem;
        border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};
        padding-bottom: 1rem;
        flex-wrap: wrap;
    }
`;

const AdminNavLink = styled(NavLink)`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 1.2rem;
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid transparent;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textMedium};
    text-decoration: none;
    transition: all 0.2s ease-in-out;

    &:hover {
        color: ${({ theme }) => theme.colors.primaryDark};
        background-color: ${({ theme }) => theme.colors.neutralLight};
    }

    &.active {
        background-color: ${({ theme }) => theme.colors.primary};
        color: white;
        box-shadow: ${({ theme }) => theme.shadows.sm};
    }
`;

const MobileAdminGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.5rem;
    margin-bottom: 1rem; /* Zmniejszony margines pod siatką */

    @media (min-width: 768px) {
        display: none;
    }
`;

const MobileAdminButton = styled(NavLink)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    padding: 0.5rem 0.25rem;
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid ${({ theme }) => theme.colors.borderColor};
    color: ${({ theme }) => theme.colors.textDark};
    font-weight: 500;
    font-size: 0.65rem;
    text-align: center;
    text-decoration: none;
    transition: all 0.2s ease-in-out;

    svg {
        width: 22px;
        height: 22px;
        color: ${({ theme }) => theme.colors.primary};
        transition: all 0.2s ease-in-out;
        margin-bottom: 2px;
    }

    &.active {
        background-color: ${({ theme }) => theme.colors.primary};
        color: white;
        border-color: ${({ theme }) => theme.colors.primaryDark};
        transform: translateY(-2px);
        box-shadow: ${({ theme }) => theme.shadows.md};

        svg {
            color: white;
            transform: scale(1.05);
        }
    }
`;

const adminNavItems = [
    { to: "organizations", icon: Building, label: "Organizacje" },
    { to: "app-settings", icon: ToggleRight, label: "Funkcje" },
    { to: "role-permissions", icon: Users, label: "Uprawnienia" },
    { to: "theme", icon: Brush, label: "Wygląd" },
    { to: "bottom-nav", icon: List, label: "Dolny Pasek" },
    { to: "pending-profiles", icon: ShieldCheck, label: "Profile" },
    { to: "invite-global", icon: UserPlus, label: "Zaproś" },
];


const SystemAdminDashboard = () => {
    const location = useLocation();
    const isOnDashboardIndex = location.pathname === '/app/system-admin-panel' || location.pathname === '/app/system-admin-panel/';

    return (
        <PageContentWrapper className="custom-scrollbar">
            <AdminLayout>
                <PageHeader>Panel Administratora Systemu</PageHeader>

                <AdminNav>
                    {adminNavItems.map(item => (
                        <AdminNavLink key={item.to} to={item.to}>
                            <item.icon size={18} />
                            {item.label}
                        </AdminNavLink>
                    ))}
                </AdminNav>

                <MobileAdminGrid>
                    {adminNavItems.map(item => (
                        <MobileAdminButton key={item.to} to={item.to}>
                            <item.icon />
                            <span>{item.label}</span>
                        </MobileAdminButton>
                    ))}
                </MobileAdminGrid>

                {isOnDashboardIndex && <Navigate to="organizations" replace />}

                <Routes>
                    <Route path="organizations" element={<OrganizationManagement />} />
                    <Route path="organization/:orgId" element={<OrganizationDetail />} />
                    <Route path="app-settings" element={<SystemFeatureManagement />} />
                    <Route path="role-permissions" element={<RolePermissionManagement />} />
                    <Route path="theme" element={<ThemeManagement />} />
                    <Route path="bottom-nav" element={<BottomNavManagement />} />
                    <Route path="pending-profiles" element={<PendingProfiles />} />
                    <Route path="invite-global" element={
                        <InviteForm
                            title="Zaproś Globalnego Psychologa"
                            description="Użytkownik nie będzie przypisany do żadnej organizacji."
                            availableRoles={{ "ROLE_PSYCHOLOGIST": "Psycholog (ePsycholog)" }}
                            endpoint="/system-admin/invitations/global"
                        />
                    } />
                </Routes>
            </AdminLayout>
        </PageContentWrapper>
    );
};

export default SystemAdminDashboard;